import type { CSSProperties } from 'react';
import { isMasked, MASKED_AMOUNT } from '../lib/format';

/**
 * The one way money renders.
 *
 * Three sizes, one convention: dollars carry the weight, cents are present but
 * de-emphasised — the eye reads $1,849 and can find the .32 if it wants to.
 * Negative amounts always pair the − sign with colour, never colour alone,
 * because red/green is exactly the distinction colour-blind users lose.
 *
 * `hero` and `stat` use the display face like a headline; `row` stays tabular
 * for columns. All three carry `vd-num` (tabular figures) and `vd-money`
 * (the privacy mask's blur target — see useMasked).
 */
export type MoneySize = 'hero' | 'stat' | 'row';

const SIZES: Record<MoneySize, { main: CSSProperties; cents: CSSProperties }> = {
  hero: {
    main: {
      fontFamily: 'var(--vn-font-display)', fontSize: 40, fontWeight: 700,
      letterSpacing: '-0.03em', lineHeight: 1.05,
    },
    cents: { fontSize: 20, fontWeight: 600 },
  },
  stat: {
    main: {
      fontFamily: 'var(--vn-font-display)', fontSize: 22, fontWeight: 700,
      letterSpacing: '-0.02em', lineHeight: 1.1,
    },
    cents: { fontSize: 13, fontWeight: 600 },
  },
  row: {
    main: { fontSize: 13, fontWeight: 600, lineHeight: 1.2 },
    cents: { fontSize: 10.5, fontWeight: 500 },
  },
};

export function Money({
  value, size = 'row', cents, tone = 'auto', suffix, style,
}: {
  value: number;
  size?: MoneySize;
  /** Show cents. Defaults on for `row`, off for the display sizes. */
  cents?: boolean;
  /**
   * 'auto' colours negatives red and leaves positives alone — the right default
   * for amounts. 'signed' colours both directions (for deltas, where green
   * "up" is information). 'plain' opts out entirely.
   */
  tone?: 'auto' | 'signed' | 'plain';
  /** e.g. "/mo" — rendered small and muted. */
  suffix?: string;
  style?: CSSProperties;
}) {
  const s = SIZES[size];

  // Masked: same element, same metrics, bullets for digits — the Robinhood
  // treatment. Direction and colour are hidden too; a red "$••••" would leak
  // the one bit the mask exists to hide.
  if (isMasked()) {
    return (
      <span className="vd-num vd-money" style={{ whiteSpace: 'nowrap', ...s.main, ...style }}>
        {MASKED_AMOUNT}
        {suffix && (
          <span style={{
            fontSize: s.cents.fontSize, fontWeight: 400, letterSpacing: 0,
            color: 'var(--vn-text-muted)', fontFamily: 'var(--vn-font-body)',
          }}>
            {suffix}
          </span>
        )}
      </span>
    );
  }

  const showCents = cents ?? size === 'row';
  const negative = value < 0;
  const abs = Math.abs(value);
  const whole = Math.floor(abs);
  const frac = Math.round((abs - whole) * 100);

  const colour =
    tone === 'plain' ? undefined
    : negative ? 'var(--vn-danger)'
    : tone === 'signed' && value > 0 ? 'var(--vn-success)'
    : undefined;

  return (
    <span className="vd-num vd-money" style={{ color: colour, whiteSpace: 'nowrap', ...s.main, ...style }}>
      {negative && '−'}{tone === 'signed' && value > 0 && '+'}
      ${whole.toLocaleString('en-US')}
      {showCents && (
        <span style={{ ...s.cents, color: colour ?? 'var(--vn-text-muted)' }}>
          .{String(frac).padStart(2, '0')}
        </span>
      )}
      {suffix && (
        <span style={{
          fontSize: s.cents.fontSize, fontWeight: 400, letterSpacing: 0,
          color: 'var(--vn-text-muted)', fontFamily: 'var(--vn-font-body)',
        }}>
          {suffix}
        </span>
      )}
    </span>
  );
}
